import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import oneSpaceApi from '../../lib/onespace-api';

// Interface voor een tijdsregistratie
interface TimeEntry {
  id: string;
  projectId: string;
  description: string;
  startTime: string;  // ISO date string
  endTime?: string;   // ISO date string (optional for ongoing entries)
  duration: number;   // in seconds
  isBillable: boolean;
}

// Interface voor een project
interface Project {
  id: string;
  name: string;
  color: string;
  client?: string;
}

// Interface voor de widget properties
interface TimeTrackerWidgetProps {
  title?: string;
  projectsUrl?: string;
  timeEntriesUrl?: string;
  readOnly?: boolean;
}

// Helper functie voor unieke IDs
const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

// Helper functie voor formatteren van seconden naar leesbare tijd
const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Helper functie voor het bepalen van de dag (vandaag, gisteren, of datum)
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (date.toDateString() === today.toDateString()) {
    return 'Vandaag';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Gisteren';
  } else {
    return date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }
};

// Dummy projecten voor de demo
const DEMO_PROJECTS: Project[] = [
  { id: 'p1', name: 'Website Development', color: '#4f46e5', client: 'Acme Corp' },
  { id: 'p2', name: 'App Design', color: '#16a34a', client: 'TechStart' },
  { id: 'p3', name: 'Marketing', color: '#ea580c', client: 'Local Business' },
  { id: 'p4', name: 'Research', color: '#0ea5e9', client: 'Internal' },
];

// Dummy tijdsregistraties voor de demo
const DEMO_TIME_ENTRIES: TimeEntry[] = [
  {
    id: 'e1',
    projectId: 'p1',
    description: 'Frontend development',
    startTime: new Date(new Date().setHours(new Date().getHours() - 3)).toISOString(),
    endTime: new Date(new Date().setHours(new Date().getHours() - 1)).toISOString(),
    duration: 7200, // 2 hours
    isBillable: true,
  },
  {
    id: 'e2',
    projectId: 'p2',
    description: 'Design mockups',
    startTime: new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(),
    endTime: new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(),
    duration: 5400, // 1.5 hours
    isBillable: true,
  },
  {
    id: 'e3',
    projectId: 'p3',
    description: 'Content planning',
    startTime: new Date(new Date().setDate(new Date().getDate() - 2)).toISOString(),
    endTime: new Date(new Date().setDate(new Date().getDate() - 2)).toISOString(),
    duration: 3600, // 1 hour
    isBillable: false,
  },
];

const TimeTrackerWidget: React.FC<TimeTrackerWidgetProps> = ({
  title = 'Urenregistratie',
  projectsUrl = '/api/projects',
  timeEntriesUrl = '/api/time-entries',
  readOnly = false,
}) => {
  // State voor projecten
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  
  // State voor tijdsregistraties
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [isLoadingEntries, setIsLoadingEntries] = useState(true);
  
  // State voor actieve timer
  const [activeEntry, setActiveEntry] = useState<TimeEntry | null>(null);
  const [elapsed, setElapsed] = useState<number>(0);
  const timerRef = useRef<number | null>(null);
  
  // State voor het formulier om een nieuwe tijdsregistratie toe te voegen
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEntry, setNewEntry] = useState<{
    projectId: string;
    description: string;
    hours: string;
    minutes: string;
    isBillable: boolean;
  }>({
    projectId: '',
    description: '',
    hours: '0',
    minutes: '0',
    isBillable: true,
  });
  
  // State voor weergavemodus (dag of week)
  const [viewMode, setViewMode] = useState<'day' | 'week'>('day');
  
  // State voor geselecteerde dag voor dagoverzicht
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  
  // Laad projecten en tijdsregistraties bij het laden van de component
  useEffect(() => {
    // Fetch projecten
    const fetchProjects = async () => {
      setIsLoadingProjects(true);
      try {
        // In een echte applicatie haal je de data op van de API
        // const response = await oneSpaceApi.get(projectsUrl);
        // setProjects(response.data);
        
        // Voor demo gebruiken we dummydata
        setTimeout(() => {
          setProjects(DEMO_PROJECTS);
          setIsLoadingProjects(false);
        }, 500);
      } catch (error) {
        console.error('Error fetching projects:', error);
        setIsLoadingProjects(false);
      }
    };
    
    // Fetch tijdsregistraties
    const fetchTimeEntries = async () => {
      setIsLoadingEntries(true);
      try {
        // In een echte applicatie haal je de data op van de API
        // const response = await oneSpaceApi.get(timeEntriesUrl);
        // setTimeEntries(response.data);
        
        // Voor demo gebruiken we dummydata
        setTimeout(() => {
          setTimeEntries(DEMO_TIME_ENTRIES);
          setIsLoadingEntries(false);
        }, 500);
      } catch (error) {
        console.error('Error fetching time entries:', error);
        setIsLoadingEntries(false);
      }
    };
    
    fetchProjects();
    fetchTimeEntries();
    
    // Cleanup timers bij unmount
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
    };
  }, [projectsUrl, timeEntriesUrl]);
  
  // Timer update functie
  useEffect(() => {
    if (activeEntry) {
      // Bereken de verstreken tijd
      const startTime = new Date(activeEntry.startTime).getTime();
      const initialElapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsed(initialElapsed);
      
      // Start een timer die elke seconde bijwerkt
      timerRef.current = window.setInterval(() => {
        setElapsed(prev => prev + 1);
      }, 1000);
    } else {
      // Stop de timer als er geen actieve tijdsregistratie is
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
    };
  }, [activeEntry]);
  
  // Start een timer voor een project
  const startTimer = (projectId: string) => {
    // Stop eerst een eventuele lopende timer
    if (activeEntry) {
      stopTimer();
    }
    
    const newTimeEntry: TimeEntry = {
      id: generateId(),
      projectId,
      description: '',
      startTime: new Date().toISOString(),
      duration: 0,
      isBillable: true,
    };
    
    setActiveEntry(newTimeEntry);
    setElapsed(0);
    
    // In een echte applicatie zou je deze tijdsregistratie naar de API sturen
    // oneSpaceApi.post(timeEntriesUrl, newTimeEntry);
  };
  
  // Stop de actieve timer
  const stopTimer = async () => {
    if (!activeEntry) return;
    
    const endTime = new Date().toISOString();
    const duration = elapsed;
    
    const completedEntry: TimeEntry = {
      ...activeEntry,
      endTime,
      duration,
    };
    
    // Update lokale staat
    setTimeEntries(prev => [completedEntry, ...prev]);
    setActiveEntry(null);
    
    // In een echte applicatie zou je deze tijdsregistratie updaten in de API
    // await oneSpaceApi.put(`${timeEntriesUrl}/${completedEntry.id}`, completedEntry);
  };
  
  // Bewerk de beschrijving van de actieve timer
  const updateActiveDescription = (description: string) => {
    if (!activeEntry) return;
    
    setActiveEntry(prev => {
      if (!prev) return null;
      return {
        ...prev,
        description,
      };
    });
  };
  
  // Handmatig een tijdsregistratie toevoegen
  const addManualEntry = async () => {
    const { projectId, description, hours, minutes, isBillable } = newEntry;
    
    if (!projectId || (!hours && !minutes)) return;
    
    const now = new Date();
    const duration = (parseInt(hours) * 3600) + (parseInt(minutes) * 60);
    
    // Bereken de starttijd door de duur af te trekken van de huidige tijd
    const startTime = new Date(now.getTime() - (duration * 1000)).toISOString();
    
    const newTimeEntry: TimeEntry = {
      id: generateId(),
      projectId,
      description,
      startTime,
      endTime: now.toISOString(),
      duration,
      isBillable,
    };
    
    // Update lokale staat
    setTimeEntries(prev => [newTimeEntry, ...prev]);
    
    // Reset formulier
    setNewEntry({
      projectId: '',
      description: '',
      hours: '0',
      minutes: '0',
      isBillable: true,
    });
    
    setShowAddForm(false);
    
    // In een echte applicatie zou je deze tijdsregistratie naar de API sturen
    // await oneSpaceApi.post(timeEntriesUrl, newTimeEntry);
  };
  
  // Filter tijdsregistraties op basis van de geselecteerde dag
  const getFilteredEntries = (): TimeEntry[] => {
    if (viewMode === 'day') {
      // Filter op geselecteerde dag
      return timeEntries.filter(entry => {
        const entryDate = new Date(entry.startTime);
        return (
          entryDate.getDate() === selectedDate.getDate() &&
          entryDate.getMonth() === selectedDate.getMonth() &&
          entryDate.getFullYear() === selectedDate.getFullYear()
        );
      });
    } else {
      // Filter op huidige week
      const weekStart = new Date(selectedDate);
      weekStart.setDate(selectedDate.getDate() - selectedDate.getDay() + 1); // Maandag
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6); // Zondag
      
      return timeEntries.filter(entry => {
        const entryDate = new Date(entry.startTime);
        return entryDate >= weekStart && entryDate <= weekEnd;
      });
    }
  };
  
  // Bereken de totale duur van de gefilterde tijdsregistraties
  const calculateTotalDuration = (): number => {
    return getFilteredEntries().reduce((total, entry) => total + entry.duration, 0);
  };
  
  // Formateer de totale duur voor weergave
  const formattedTotalDuration = (): string => {
    const totalSeconds = calculateTotalDuration();
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    
    return `${hours}u ${minutes}m`;
  };
  
  // Ga naar de vorige dag/week
  const goToPrevious = () => {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      if (viewMode === 'day') {
        newDate.setDate(prev.getDate() - 1);
      } else {
        newDate.setDate(prev.getDate() - 7);
      }
      return newDate;
    });
  };
  
  // Ga naar de volgende dag/week
  const goToNext = () => {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      if (viewMode === 'day') {
        newDate.setDate(prev.getDate() + 1);
      } else {
        newDate.setDate(prev.getDate() + 7);
      }
      return newDate;
    });
  };
  
  // Ga naar vandaag
  const goToToday = () => {
    setSelectedDate(new Date());
  };
  
  // Formateer de huidige weergave (dag of week)
  const formatViewDate = (): string => {
    if (viewMode === 'day') {
      return selectedDate.toLocaleDateString('nl-NL', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    } else {
      const weekStart = new Date(selectedDate);
      weekStart.setDate(selectedDate.getDate() - selectedDate.getDay() + 1); // Maandag
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6); // Zondag
      
      return `${weekStart.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })} - ${weekEnd.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
  };
  
  // Vind een project op basis van ID
  const getProject = (projectId: string): Project | undefined => {
    return projects.find(project => project.id === projectId);
  };
  
  // Render de projectknoppen voor het starten van een timer
  const renderProjectButtons = () => {
    if (isLoadingProjects) {
      return (
        <div className="animate-pulse">
          <div className="h-8 bg-primary-theme-10 rounded w-full mb-2"></div>
          <div className="h-8 bg-primary-theme-10 rounded w-full mb-2"></div>
        </div>
      );
    }
    
    if (projects.length === 0) {
      return (
        <p className="text-theme-80 text-center py-2">Geen projecten beschikbaar.</p>
      );
    }
    
    return (
      <div className="flex flex-wrap gap-2">
        {projects.map(project => (
          <Button
            key={project.id}
            onClick={() => startTimer(project.id)}
            disabled={!!activeEntry || readOnly}
            className="flex items-center gap-2 text-theme"
            variant="outline"
          >
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: project.color }}
            ></div>
            <span>{project.name}</span>
          </Button>
        ))}
      </div>
    );
  };
  
  // Render de actieve timer
  const renderActiveTimer = () => {
    if (!activeEntry) return null;
    
    const project = getProject(activeEntry.projectId);
    
    return (
      <div className="mb-4 p-3 border border-primary-theme-20 rounded-lg bg-primary-theme-10">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: project?.color || '#cccccc' }}
            ></div>
            <span className="font-medium">{project?.name || 'Onbekend project'}</span>
          </div>
          <div className="text-xl font-bold font-mono text-theme">{formatDuration(elapsed)}</div>
        </div>
        
        <input
          type="text"
          value={activeEntry.description}
          onChange={(e) => updateActiveDescription(e.target.value)}
          placeholder="Voeg een beschrijving toe..."
          className="w-full mb-2 p-2 border border-theme-10 rounded bg-primary-theme"
        />
        
        <div className="flex justify-end">
          <Button
            onClick={stopTimer}
            className="bg-secondary-theme hover:bg-secondary-theme/80 text-black"
          >
            Stop
          </Button>
        </div>
      </div>
    );
  };
  
  // Render het formulier voor het handmatig toevoegen van tijd
  const renderAddTimeForm = () => {
    if (!showAddForm) return null;
    
    return (
      <div className="mb-4 p-3 border border-primary-theme-20 rounded-lg bg-primary-theme-10">
        <h4 className="font-medium text-theme mb-2">Tijd toevoegen</h4>
        
        <div className="space-y-2">
          <div>
            <label className="block text-theme-80 text-sm mb-1">Project</label>
            <select
              value={newEntry.projectId}
              onChange={(e) => setNewEntry({ ...newEntry, projectId: e.target.value })}
              className="w-full p-2 border border-theme-10 rounded bg-primary-theme"
            >
              <option value="">Selecteer een project</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-theme-80 text-sm mb-1">Beschrijving</label>
            <input
              type="text"
              value={newEntry.description}
              onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
              placeholder="Wat heb je gedaan?"
              className="w-full p-2 border border-theme-10 rounded bg-primary-theme"
            />
          </div>
          
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-theme-80 text-sm mb-1">Uren</label>
              <input
                type="number"
                min="0"
                max="24"
                value={newEntry.hours}
                onChange={(e) => setNewEntry({ ...newEntry, hours: e.target.value })}
                className="w-full p-2 border border-theme-10 rounded bg-primary-theme"
              />
            </div>
            <div className="flex-1">
              <label className="block text-theme-80 text-sm mb-1">Minuten</label>
              <input
                type="number"
                min="0"
                max="59"
                value={newEntry.minutes}
                onChange={(e) => setNewEntry({ ...newEntry, minutes: e.target.value })}
                className="w-full p-2 border border-theme-10 rounded bg-primary-theme"
              />
            </div>
          </div>
          
          <div className="flex items-center mt-1">
            <input
              type="checkbox"
              id="isBillable"
              checked={newEntry.isBillable}
              onChange={(e) => setNewEntry({ ...newEntry, isBillable: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="isBillable" className="text-theme-80 text-sm">
              Declarabel
            </label>
          </div>
          
          <div className="flex justify-end space-x-2 pt-2">
            <Button
              onClick={() => setShowAddForm(false)}
              variant="outline"
              className="text-theme border-theme-20"
            >
              Annuleren
            </Button>
            <Button
              onClick={addManualEntry}
              className="bg-secondary-theme hover:bg-secondary-theme/80 text-black"
            >
              Toevoegen
            </Button>
          </div>
        </div>
      </div>
    );
  };
  
  // Render de tijdsregistraties lijst
  const renderTimeEntries = () => {
    const filteredEntries = getFilteredEntries();
    
    if (isLoadingEntries) {
      return (
        <div className="animate-pulse space-y-2">
          <div className="h-16 bg-primary-theme-10 rounded w-full"></div>
          <div className="h-16 bg-primary-theme-10 rounded w-full"></div>
        </div>
      );
    }
    
    if (filteredEntries.length === 0) {
      return (
        <p className="text-theme-80 text-center py-4">
          Geen tijdsregistraties voor deze {viewMode === 'day' ? 'dag' : 'week'}.
        </p>
      );
    }
    
    // Groepeer tijdsregistraties per dag
    const entriesByDate: Record<string, TimeEntry[]> = {};
    
    filteredEntries.forEach(entry => {
      const dateKey = formatDate(entry.startTime);
      if (!entriesByDate[dateKey]) {
        entriesByDate[dateKey] = [];
      }
      entriesByDate[dateKey].push(entry);
    });
    
    return (
      <div className="space-y-4">
        {Object.entries(entriesByDate).map(([date, entries]) => (
          <div key={date}>
            <h4 className="font-medium text-theme-80 text-sm mb-2">{date}</h4>
            <div className="space-y-2">
              {entries.map(entry => {
                const project = getProject(entry.projectId);
                const startTime = new Date(entry.startTime);
                const endTime = entry.endTime ? new Date(entry.endTime) : null;
                
                const timeString = endTime 
                  ? `${startTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} - ${endTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`
                  : startTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
                
                return (
                  <div 
                    key={entry.id} 
                    className="p-3 border border-theme-10 rounded-md bg-primary-theme-10 flex justify-between items-center"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: project?.color || '#cccccc' }}
                        ></div>
                        <span className="font-medium">{project?.name || 'Onbekend project'}</span>
                        {entry.isBillable && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Declarabel</span>
                        )}
                      </div>
                      
                      {entry.description && (
                        <p className="text-sm text-theme-80 ml-5 mt-1">{entry.description}</p>
                      )}
                      
                      <div className="text-xs text-theme-80 ml-5 mt-1">{timeString}</div>
                    </div>
                    
                    <div className="font-mono text-theme">
                      {formatDuration(entry.duration).slice(0, 5)} {/* Show only HH:MM */}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xl font-semibold">{title}</CardTitle>
        <div className="flex items-center space-x-2">
          {!readOnly && (
            <Button
              size="sm"
              onClick={() => setShowAddForm(true)}
              className="h-7 text-xs bg-secondary-theme hover:bg-secondary-theme/80 text-black"
              disabled={!!activeEntry || showAddForm}
            >
              + Tijd
            </Button>
          )}
          
          <div className="flex bg-primary-theme-10 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('day')}
              className={`px-2 py-1 text-xs rounded-md ${
                viewMode === 'day' 
                  ? 'bg-secondary-theme text-black' 
                  : 'text-theme'
              }`}
            >
              Dag
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-2 py-1 text-xs rounded-md ${
                viewMode === 'week' 
                  ? 'bg-secondary-theme text-black' 
                  : 'text-theme'
              }`}
            >
              Week
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!readOnly && (
          <div className="mb-4">
            <h4 className="font-medium text-theme mb-2">Timer starten</h4>
            {renderProjectButtons()}
          </div>
        )}
        
        {renderActiveTimer()}
        
        {!readOnly && renderAddTimeForm()}
        
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2 text-theme border-theme-20"
              onClick={goToPrevious}
            >
              &lt;
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2 text-theme border-theme-20"
              onClick={goToToday}
            >
              Vandaag
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2 text-theme border-theme-20"
              onClick={goToNext}
            >
              &gt;
            </Button>
          </div>
          
          <div className="text-sm font-medium text-theme">
            {formatViewDate()}
          </div>
          
          <div className="text-sm">
            Totaal: <span className="font-medium">{formattedTotalDuration()}</span>
          </div>
        </div>
        
        {renderTimeEntries()}
      </CardContent>
    </Card>
  );
};

export default TimeTrackerWidget; 